const {observable} = require("mobx");
const {report, reportAndThrow} = require("helpers/errors");
const assign = require("object-assign");
const {createPipeline} = require("cleopatra");
const {createPromiseThrottle} = require("helpers/throttle");
const {searchObject, queryObject, pluckKeysExcept, decodeObservable} = require("helpers/object");
const {toQueryString} = require("helpers/url");
const Flash = require("components/singletons/Flash");
const _ = require("lodash");
const downloadjs = require("downloadjs");
const shortid = require("shortid");

class ApiStore {

    @observable collection = [];
    @observable constraints = {};
    @observable metadata = {};
    @observable translations = {};

    @observable loadingActive = false;
    @observable loadingCollection = false;

    @observable frozen = new Set();

    constructor(collection = [], constraints = {}, metadata = {}, options = {}) {
        this.collection         = collection;
        this.constraints        = constraints;
        this.metadata           = metadata;
        this.temp               = constraints;
        this.initialConstraints = {...constraints};

        this.options = assign({
            autoFetch: true,
            changeDelay: 500,

        }, options);

        // Throttle constraint change
        const throttle = createPromiseThrottle(this.options.changeDelay);

        // Update constraints on capture
        this.constraintChange.capture(constraints => {
            this.constraints = constraints;
            this.loadingCollection = true;
            return throttle().then(() => {
                return this.options.autoFetch ? this.fetch() : this.collection;
            }).catch(err => {
                if(err && err.message !== 'STOPPED_BY_THROTTLE') {
                    throw err;
                }
            });
        });

        // Update collection on collection change
        this.collectionChange.capture(collection => {
            this.frozen.forEach(frozen => {
                let includes = collection.find(item => item.id === frozen);

                if (!includes) {
                    collection.unshift(this.findById(frozen));
                }
            });

            this.collection = collection
        });

    }

    /////////////////////////////////
    // Configuration & Hooks
    /////////////////////////////////

    constraintChange = createPipeline().report(reportAndThrow);
    collectionChange = createPipeline().report(reportAndThrow);

    get loading() {
        return this.loadingActive || this.loadingCollection;
    }

    get endpoint() {
        return this.options.endpoint;
    }

    /////////////////////////////////
    // API Methods
    /////////////////////////////////

    // Crud - create
    create(data, options = {}, alert = true) {
        this.loadingCollection = true;

        return axios.post(this.endpoint, data, options)
            .then(response => {
                this.response = {type: 'success', msg: response.data.message};
                return response;
            })
            .catch(this.handleErrors)
            .finally(() => {
                this.loadingCollection = false;
                if (alert === true) {
                    this.flashResponse();
                }
            });
    }

    // cRud - find
    read(id, data, options = {}) {
        this.loadingActive = true;

        return axios.get(`${this.endpoint}/${id}`, data, options)
            .then(res => {
                if (res.data.translations) {
                    this.translations = res.data.translations;
                }

                return res.data.payload;
            })
            .catch(reportAndThrow)
            .finally(() => this.loadingActive = false);
    }

    // cRud - index
    fetch() {
        this.loadingCollection = true;

        return axios.get(`${this.endpoint}?${toQueryString(decodeObservable(this.resolveConstraints()))}`)
            .then(response => {
                this.executeCallbacks(this.onFetchCallbacks, response);

                return response;
            })
            .then(response => {
                if (response.data.translations) {
                    this.translations = response.data.translations;
                }

                return this.collectionChange.dispatch(response.data.payload.collection).then(collection => {
                    // update metadata
                    this.metadata = pluckKeysExcept(response.data.payload, ["collection"]);
                    return collection;
                });
            })
            .catch(reportAndThrow)
            .finally(() => {
                this.loadingCollection = false
                return this.collection;
            });
    }

    // crUd - update
    update(id, data, options = {}, alert = true) {
        this.loadingCollection = true;

        let request = axios.post(`${this.endpoint}/${id}`, data, options)
            .then(response => {
                this.response = {type: 'success', msg: response.data.message};
                return response;
            });

        request.catch(this.handleErrors)
            .finally(() => {
                this.loadingCollection = false;
                this.flashResponse(alert);
            });

        return request;
    }

    // cruD - delete
    delete(id, options = {}, alert = true) {
        this.loadingCollection = true;

        let request = axios.delete(`${this.endpoint}/${id}`, null, options)
            .then(() => {
                this.response = {type: 'success', msg: 'Entry deleted'};
            });

        request.catch(this.handleErrors)
            .finally(() => {
                this.loadingCollection = false;
                this.flashResponse(alert);
            });

        return request;
    }

    // Mass actions
    massAction(action, ids, data = {}, options = {}) {
        this.loadingCollection = true;

        action = action.split('.').pop();
        data.keys = ids;

        let response = axios.post(`${this.endpoint}/mass_action/${action}`, data, options)
            .then((response) => {
                this.response = {type: 'success', msg: 'Mass action success.'};
                return response;
            });

        response.catch(this.handleErrors);
        response.finally(() => {
            this.loadingCollection = false;
            this.flashResponse();
        });

        return response;
    }

    // Custom actions
    customAction(action, id, data = {}, options = {}, alert = true) {
        this.loadingCollection = true;
        const base = `${this.endpoint}/action/${action}`;
        const endpoint = id ?
            base + `/${id}` :
            base;

        options.method = 'PUT';
        options.url = endpoint;
        options.data = data;

        let response = axios(options)
            .then((data) => {
                this.response = {type: 'success', msg: 'Custom action success.'};
                if (data.data && data.data.payload) {
                    data = data.data.payload;
                }
                if (data.message) {
                    this.response.msg = data.message;
                }
                if (data.response_type) {
                    this.response.type = data.response_type;
                }
                if (typeof(data.status) !== 'undefined') {
                    this.response.type = (!!data.status) ? 'success' : 'error';
                }
                return data;
            });

        response.catch(this.handleErrors)
            .finally((data) => {
                this.loadingCollection = false;
                if (alert === true) {
                    this.flashResponse();
                }
                return data;
            });

        return response;
    }

    custom(axios_params, alert = true) {
        this.loadingCollection = true;

        let response = axios(axios_params)
            .then((data) => {
                this.response = {type: 'success', msg: 'Custom action success.'};
                if (data.data && data.data.payload) {
                    data = data.data.payload;
                }
                if (data.message) {
                    this.response.msg = data.message;
                }
                if (typeof(data.status) !== 'undefined') {
                    this.response.type = (!!data.status) ? 'success' : 'error';
                }
                return data;
            });

        response.catch(this.handleErrors)
            .finally((data) => {
                this.loadingCollection = false;
                if (alert === true) {
                    this.flashResponse();
                }
                return data;
            });

        return response;
    }

    /////////////////////////////////
    // Downloading
    /////////////////////////////////

    download(action = 'download', id = null, data = {}) {
        if (id === null) {
            id = this.active.id;
        }
        return this.customAction(action, id, data, {
            responseType: 'arraybuffer',
            headers: {
                Accept: 'application/pdf',
            }
        }, false).then(this.handleDownload.bind(this));
    }

    downloadMassAction(action = 'download', ids = []) {

        return this.massAction(action, ids, {}, {
            responseType: 'arraybuffer',
            headers: {
                Accept: 'application/pdf',
            }
        }, false).then(this.handleDownload.bind(this));
    }

    handleDownload(response) {
        const content = response.headers['content-type'];
        downloadjs(response.data, response.headers['content-disposition'].match(/filename="?([^";]*)"?;?/)[1], content)
    }

    /////////////////////////////////
    // Api helpers
    /////////////////////////////////

    resetConstraints(constraints) {
        const newConstraints = {...this.initialConstraints, ...constraints};

        return this.constraintChange.dispatch(newConstraints);
    }

    constrain(constraints, append_function = false) {

        if (typeof  constraints === "function" && append_function === false) {
            this.temp = assign(this.temp, constraints(decodeObservable(this.constraints)));
        }
        else {
            this.temp = assign(this.temp, constraints);

            if(Object.values(constraints)[0] === null) {
                delete this.temp[Object.keys(constraints)[0]];
            }

        }

        return this.debouncedConstrain();
    }

    debouncedConstrain = _.throttle(() => {
        return this.constraintChange.dispatch(this.temp);
    }, 50, {leading: false});

    resolveConstraints() {
        return Object.keys(this.constraints).reduce((result, key) => {
            let toAdd = {};

            let constraint = this.constraints[key];

            if (typeof(constraint) === 'function') {
                toAdd = {...constraint()};
            } else {
                toAdd[key] = constraint;
            }

            toAdd = _.pickBy(toAdd, _.identity);

            return {...result, ...toAdd};
        }, {});
    }

    append(constraints) {
        // merge constraints
        constraints = assign({}, decodeObservable(this.resolveConstraints()), constraints);

        return axios.get(`${this.endpoint}?${toQueryString(constraints)}`)
            .then(response => {
                const collection = decodeObservable(this.collection);

                // Merge collections
                response.data.collection.forEach(element => {
                    const index = collection.findIndex(el => el.id === element.id);

                    // remove old entry
                    if (index > -1) {
                        collection.splice(index, 1);
                    }

                    collection.push(element);
                });

                return this.collectionChange.dispatch(collection).then(collection => {
                    // update metadata
                    this.metadata = pluckKeysExcept(response.data, ["collection"]);
                    return collection;
                });
            });
    }

    clearCollection() {
        return this.collectionChange.dispatch([]);
    }

    /////////////////////////////////
    // Response helpers
    /////////////////////////////////

    handleErrors = (xhr) => {
        this.response = {type: 'error', msg: xhr.response.data.message, errors: xhr.response.data.errors};
        reportAndThrow(xhr);
    }

    flashResponse(flash_all = true) {
        const response = this.response ?
            this.response :
            {type: 'error', msg: 'Noget gik galt'};

        let {msg, type} = response;

        if (msg === 'The given data was invalid.') {
            msg = translate('Der var en fejl i indtastningen. Tjek felterne for flere detaljer.');
        }

        if (flash_all === false && type !== 'error') {
            return;
        }

        Flash[type](msg);
        //this.response = null;
    }

    /////////////////////////////////
    // Collection methods
    /////////////////////////////////

    findById(id) {

        const collection = decodeObservable(this.collection);
        const index = collection.findIndex(el => el.id === id);

        return index > -1 ?
            collection[index] :
            null;
    }

    getMeta(query, defaults = null) {
        const meta = decodeObservable(this.metadata);

        return queryObject(meta, query, defaults);
    }

    emptyCollection() {
        return this.collectionChange.dispatch([]);
    }

    /////////////////////////////////
    // Freezing
    /////////////////////////////////

    getFrozen() {
        return Array.from(this.frozen);
    }

    isFrozen(id) {
        return this.frozen.has(id);
    }

    clearFrozen() {
        this.frozen.clear();
    }

    toggleFrozen(id) {
        this.isFrozen(id) ?
            this.frozen.delete(id) :
            this.frozen.add(id);

        this._updateFrozen();
    }

    replaceFrozen(ids) {
        this.frozen = new Set(ids);
        return;
        this.clearFrozen();
        ids.forEach(id => {
            this.toggleFrozen(id);
        })
    }

    _updateFrozen() {
        const values = Array.from(this.frozen.values());

        this.frozen = new Set(values);
    }

    /////////////////////////////////
    // Translations
    /////////////////////////////////

    translate(key, count = null) {

        let ref = this.translations;

        if (Object.keys(ref).length === 0) {
            return '';
        }

        const query = (Number.isInteger(count)) ?
            key + ((count > 1 || !count) ? '.pluralis' : '.singularis') :
            key;

        return searchObject(ref, query, key);
    }

    /////////////////////////////////
    // Callbacks
    /////////////////////////////////

    executeCallbacks(callbacks, ...params) {

        callbacks = Object.values(callbacks);

        if (callbacks.length > 0) {
            callbacks.forEach(callback => {
                if (typeof (callback) === 'function') {
                    callback(...params);
                }
            })
        }
    }

    onFetchCallbacks = {};

    onFetch(uuid, callback) {

        if (typeof uuid === 'function') {
            callback = uuid;
            uuid = shortid.generate();
        }

        if (typeof uuid === 'string' && typeof callback === 'function') {
            this.onFetchCallbacks[uuid] = callback;
        }
    }

    unsetOnFetchCallback(uuid) {
        if(typeof this.onFetchCallbacks[uuid] !== 'undefined') {
            delete this.onFetchCallbacks[uuid];
        }
    }

}

module.exports = ApiStore;
